import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { RootState } from "app/store";
import { useScale, btnDelay } from "common/utils";
import i18n from "features/shared/i18n";
import { selectAllParentChatRoomMessageSync } from "features/message/slice";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  path: string;
  icon: string;
  labelKey: string;
  badge?: "unread" | "new";
}

const menuItems: MenuItem[] = [
  { path: "/account", icon: "person", labelKey: "MSG_PARENT_PAGE_TAB_ACCOUNT" },
  { path: "/player", icon: "group", labelKey: "MSG_PARENT_PAGE_TAB_PLAYER" },
  {
    path: "/message",
    icon: "chat",
    labelKey: "MSG_PARENT_PAGE_TAB_MESSAGE",
    badge: "unread",
  },
  {
    path: "/notification",
    icon: "notifications",
    labelKey: "MSG_PARENT_PAGE_TAB_NOTIFICATION",
    badge: "new",
  },
];

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { height, scale } = useScale();
  const navigate = useNavigate();
  const location = useLocation();

  const notification = useSelector(
    (state: RootState) => state.config.notification.parents,
  );
  const parentChatRoomMessageSync = useSelector(
    selectAllParentChatRoomMessageSync,
  );

  const hasNewNotification = notification.some(
    (n) => dayjs().diff(n.published_at, "day") < 7,
  );
  const hasUnreadMessage =
    parentChatRoomMessageSync.filter(
      (sync) =>
        dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
          dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
        ) > 0 ||
        (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null),
    ).length > 0;

  const handleNavigate = (path: string) => {
    btnDelay(() => {
      navigate(path);
      onClose();
    });
  };

  const getBadge = (item: MenuItem) => {
    if (item.badge === "unread" && hasUnreadMessage) {
      return (
        <div className="flex-col-el flex-center absolute right-2 top-1/2 -translate-y-1/2 bg-danger rounded-full w-[12px] h-[12px]" />
      );
    }
    if (item.badge === "new" && hasNewNotification) {
      return (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center px-2 h-[18px] bg-danger text-white rounded-[9px] text-[10px]">
          NEW
        </span>
      );
    }
    return null;
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-9998 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <aside
        style={{ width: 280 * scale }}
        className={`fixed left-0 top-0 h-full bg-white z-9999 shadow-xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div
          style={{
            width: 280,
            height: height / scale,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el origin-top-left"
        >
          {/* Close button */}
          <div className="flex-row-el items-center justify-end p-4 border-b border-gray2-30">
            <button
              className="flex-row-el flex-center w-[40px] h-[40px] rounded-full bg-gray2-30 hover:bg-gray2-40 transition-colors cursor-pointer border-none"
              onClick={onClose}
            >
              <span className="material-symbols-outlined text-[24px] text-textcolor-black">
                close
              </span>
            </button>
          </div>

          {/* Menu items */}
          <nav className="flex-col-el p-4 gap-2">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  className={`relative flex-row-el items-center gap-4 px-4 py-4 rounded-xl text-[24px] cursor-pointer border-none transition-all ${
                    isActive
                      ? "bg-ink text-white"
                      : "bg-transparent text-textcolor-black hover:bg-gray2-30"
                  }`}
                  onClick={() => handleNavigate(item.path)}
                >
                  <span className="material-symbols-outlined text-[24px]">
                    {item.icon}
                  </span>
                  <span>{i18n.t(item.labelKey)}</span>
                  {getBadge(item)}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
};
